import React, { Dispatch, Fragment, ReactElement, ReactFragment, SetStateAction, useState } from 'react';
import { Button }from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

// Components

import ModalHeader from '@common-components/headers/ModalHeader';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';

// Types

import {
    CommonModalProps,
    IsState,
    MemberRemovalReason,
    OnChangeArgTypes,
    PersonName
} from '@components/ssap/SsapTypes';
import { isDateAfter } from '@utils/dates/date.utils';
import { Dayjs } from 'dayjs';

interface IRemoveMemberModal extends CommonModalProps {
    modal: {
        modalData: {
            actions: {
                handleRemovingApplyingForCoverage: Function,
                handleMemberDeletion: Function
            },
            applicantGuid: string,
            applyingForCoverageIndicator: boolean | null,
            dateOfBirth: string,
            deceased: boolean | null,
            deceasedDate: string | null,
            memberRemovalReasonEnabled: boolean,
            name: PersonName,
            serverDate: Dayjs
        }
    },
    environment: {
        isState: IsState
    }
}

const RemoveMemberModal = (props: IRemoveMemberModal): ReactElement => {

    const DEATH_DATE_INPUT_NAME: string = 'deathDate';
    const DIVORCE_DATE_INPUT_NAME: string = 'divorceDate';

    const {
        actions: {
            hideModal,
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        formName = '',
        methods = { },
        modal: {
            modalData: {
                actions: {
                    handleRemovingApplyingForCoverage,
                    handleMemberDeletion
                },
                applicantGuid,
                applyingForCoverageIndicator,
                dateOfBirth: birthDate,
                deceased,
                deceasedDate,
                memberRemovalReasonEnabled,
                name,
                serverDate
            }
        },
        modalHeaderProps,
        environment: {
            isState 
        }
    } = props;

    interface LocalState {
        deathDate: string | null,
        divorceDate: string | null,
        otherDate: string | null,
        removeAction: boolean | null,
        removalReasonType: MemberRemovalReason
    }

    const INITIAL_VALUES: LocalState = {
        deathDate: deceasedDate,
        divorceDate: null,
        otherDate: null,
        removeAction: null,
        removalReasonType: MemberRemovalReason.UNSPECIFIED
    };

    const [
        localState,
        updateLocalState
    ]: [
        LocalState,
        Dispatch<SetStateAction<LocalState>>
    ] = useState<LocalState>(INITIAL_VALUES);

    const {
        deathDate,
        divorceDate,
        otherDate,
        removeAction,
        removalReasonType
    }: LocalState = localState;

    const handleHideClick = (): void => hideModal();

    const handleSaveClick = (): void => {
        const removedMemberInfo = {
            [ applicantGuid ]: {
                demographic: {
                    birthDate,
                    deathDate,
                    divorceDate,
                    name,
                    otherDate
                },
                removalReasonType
            }
        };

        if(!(formName in methods)) {
            console.log('Form name is not found in RemoveMemberModal');
            return;
        }

        let successCallbacks;

        switch(true) {
            case removeAction === true:
                successCallbacks = [
                    handleRemovingApplyingForCoverage,
                    hideModal
                ];
                break;
            case removeAction === false:
                switch(memberRemovalReasonEnabled) {
                    case true:
                        switch(removalReasonType) {
                            case INITIAL_VALUES.removalReasonType:
                                successCallbacks = [ ];
                                break;
                            default:
                                switch(true) {
                                    case removalReasonType === MemberRemovalReason.DEATH && !deathDate:
                                    case removalReasonType === MemberRemovalReason.DIVORCE && !divorceDate:
                                        successCallbacks = [ ];
                                        break;
                                    default:
                                        successCallbacks = [
                                            () => handleMemberDeletion(removedMemberInfo),
                                            hideModal
                                        ];
                                }
                                break;
                        }
                        break;
                    case false:
                        successCallbacks = [
                            () => handleMemberDeletion(removedMemberInfo),
                            hideModal
                        ];
                }
                break;
            default:
                successCallbacks = [ ];
        }

        methods[formName].onFormSubmitForValidationOnly.apply(null, [ successCallbacks, [ ] ]);
    };

    const onFieldChange = (...args: OnChangeArgTypes): void => {
        const [ , , , [ name, value ] ] = args;

        let cleanUpMerge = {};

        switch(true) {
            case name === 'removeAction' && value === true:
                cleanUpMerge = INITIAL_VALUES;
                break;
            case name === 'removeAction' && value === false && deceased:
                cleanUpMerge = { removalReasonType: MemberRemovalReason.DEATH };
                break;
            case name === 'removalReasonType' && value === MemberRemovalReason.OTHER:
                cleanUpMerge = { deathDate: null, divorceDate: null };
                break;
            case name === 'removalReasonType' && value === MemberRemovalReason.DEATH:
                cleanUpMerge = { divorceDate: null };
                break;
            case name === 'removalReasonType' && value === MemberRemovalReason.DIVORCE:
                cleanUpMerge = { deathDate: null };
                break;
            default:
                cleanUpMerge = {};
        }

        updateLocalState((state: LocalState): LocalState => ({
            ...state,
            ...cleanUpMerge,
            [ name ]: value
        }));
    };

    const helpText: { [ key: string ]: React.ReactNode | ReactFragment } = !isState.NJ
        ? {
            helpText: (
                <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                    <div className = 'usa-alert__body gi-alert__info__body' >
                        <p className = 'usa-alert__text gi-alert__info__text' >
                            <FormattedMessage id = 'modals.remove_member.remove_applying_for_coverage.help_text' />
                        </p>
                    </div>
                </div>
            )
        }
        : { helpText: <Fragment/> };

    
    const DatePickerForReason = (inputName: string): ReactElement => {
        const labelName = inputName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .split(' ')
            .map(item => item.toLowerCase())
            .join('_');

        const isFutureDate: boolean = isDateAfter(localState[inputName], serverDate, 'days');
        return (
            <UniversalInput
                label = { {
                    text: `ssap.input.label.${ labelName }`,
                    labelClass: [ 'tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label text-left' ],
                } }
                inputData = { {
                    divClass: [ 'tablet:grid-col-8' ],
                    isInputRequired: true,
                    type: INPUT_TYPES.DATE_PICKER,
                    name: inputName,
                    value: localState[inputName],
                    valuesToMatch: [ birthDate, serverDate ],
                    validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                    validationErrorMessage: inputName === DEATH_DATE_INPUT_NAME && isFutureDate
                        ? VALIDATION_ERROR_MESSAGES.DECEASED_DATE_NOT_FUTURE_DATE
                        : VALIDATION_ERROR_MESSAGES.DATE,
                    currentData: localState,
                    fields: [ ]
                } }
                errorClasses={ [ 'grid-offset-4' ] }
                inputActions={ {
                    onFieldChange
                } }
                parentForm = { {
                    name: formName
                } }
            />
        );
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head' >
                <ModalHeader { ...modalHeaderProps } />
            </div>
            <div id = 'removeMemberModal' className = 'gi-modal__body' >
                <Form { ...{ formName } } >
                    <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                        <div className = 'usa-alert__body gi-alert__info__body' >
                            <p className = 'usa-alert__text gi-alert__info__text' >
                                <FormattedMessage
                                    id = 'modals.remove_member.suggestion'
                                />
                            </p>
                        </div>
                    </div>
                    <UniversalInput
                        legend={{
                            legendText: 'ssap.input.legend.remove_member_options',
                            legendTextReplacements: {
                                personName: <strong>{ normalizeNameOfPerson(name) }</strong>
                            }
                        }}
                        label={{
                            showRequiredIcon: true,
                            ulClass: [ 'margin-b-20' ]
                        }}
                        inputData = { {
                            isInputRequired: true,
                            managingValues: {
                                isGoingToNotSeekingForCoverage: !!removeAction
                            },
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            liClass: [ 'text-left' ],
                            name: 'removeAction',
                            value: removeAction,
                            options: DROPDOWN_OPTIONS.REMOVE_MEMBER_ACTIONS,
                            optionsReplacements: {
                                personName: normalizeNameOfPerson(name),
                                ...helpText
                            },
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: localState,
                            fields: [ ]
                        } }
                        errorClasses={ [ 'margin-l-0' ] }
                        inputActions={ {
                            onFieldChange
                        } }
                    />

                    {
                        (removeAction === false && memberRemovalReasonEnabled) &&

                            <Fragment>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.remove_member_reasons'
                                    } }
                                    label = { {
                                        showRequiredIcon: true,
                                        ulClass: [ 'margin-b-20' ]
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        managingValues: {
                                            isDeathOptionShown: !applyingForCoverageIndicator,
                                            isDeathDateShown: removalReasonType === MemberRemovalReason.DEATH,
                                            isDivorceOptionDisabled: !applyingForCoverageIndicator && !!deceased,
                                            isOtherOptionDisabled: !applyingForCoverageIndicator && !!deceased,
                                            isDivorceDateShown: removalReasonType === MemberRemovalReason.DIVORCE
                                        },
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'removalReasonType',
                                        value: removalReasonType,
                                        options: DROPDOWN_OPTIONS.REMOVE_MEMBER_REASONS,
                                        optionsReplacements: {
                                            divorceDate: DatePickerForReason(DIVORCE_DATE_INPUT_NAME),
                                            deathDate: DatePickerForReason(DEATH_DATE_INPUT_NAME)
                                        },
                                        validationType: VALIDATION_TYPES.NOT_UNSPECIFIED,
                                        currentData: localState,
                                        fields: [ ]
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                    inputActions = { {
                                        onFieldChange
                                    } }
                                />
                            </Fragment>
                    }
                </Form>
            </div>
            <div className = 'gi-modal__footer' >
                <div className = 'grid-row controls' >
                    <div className = 'grid-offset-4 txt-left' >
                        <Button
                            className = 'usa-button usa-button--outline gi-button gi-button__outline'
                            onClick = { handleHideClick }
                        >
                            <FormattedMessage id = 'button.cancel' />
                        </Button>
                        <Button
                            className = 'usa-button gi-button gi-button__primary'
                            onClick = { handleSaveClick }
                            inputRef = { boundAnchor(anchor_1) }
                        >
                            <FormattedMessage id = 'button.save_continue' />
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default RemoveMemberModal;